import { createFileRoute, Link } from "@tanstack/react-router";
import logoAsset from "@/assets/psy-bloom-logo.png.asset.json";
import { useRouteHead } from "@/hooks/use-route-head";

const routeHead = {
  meta: [
    { title: "Politica de Confidențialitate | PSY BLOOM Institute" },
    {
      name: "description",
      content:
        "Politica de confidențialitate a PSY BLOOM Institute. Informații despre protecția datelor personale și drepturile utilizatorilor.",
    },
    { property: "og:title", content: "Politica de Confidențialitate | PSY BLOOM Institute" },
    {
      property: "og:description",
      content:
        "Politica de confidențialitate a PSY BLOOM Institute. Informații despre protecția datelor personale și drepturile utilizatorilor.",
    },
    { property: "og:type", content: "website" },
    { property: "og:url", content: "/politica-de-confidentialitate" },
  ],
  links: [{ rel: "canonical", href: "/politica-de-confidentialitate" }],
};

export const Route = createFileRoute("/politica-de-confidentialitate")({
  component: PoliticaConfidentialitatePage,
});

function PoliticaConfidentialitatePage() {
  useRouteHead(routeHead);
  return (
    <div className="min-h-screen bg-background">
      {/* HEADER */}
      <header className="w-full border-b border-border/60 bg-background/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="mx-auto max-w-6xl px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" aria-label="PSY BLOOM Institute">
            <img src={logoAsset.url} alt="PSY BLOOM Institute" className="h-9 w-auto" width={180} height={36} />
          </Link>
          <a
            href="https://wa.me/40700000000"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center rounded-full bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground transition-all hover:bg-primary/90 hover:shadow-[var(--shadow-card)]"
          >
            Programează o discuție
          </a>
        </div>
      </header>

      <main>
        <section className="py-16 md:py-24">
          <div className="mx-auto max-w-3xl px-6">
            <h1 className="text-4xl md:text-5xl leading-[1.1] mb-6 text-balance">
              Politica de Confidențialitate
            </h1>
            <p className="text-sm text-muted-foreground mb-12">
              Ultima actualizare: iunie 2026
            </p>

            <div className="space-y-12 text-base md:text-lg text-muted-foreground leading-relaxed">
              <div>
                <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4">
                  1. Cine suntem
                </h2>
                <div className="space-y-4">
                  <p>
                    Website-ul PSY BLOOM Institute este operat de:
                  </p>
                  <div className="pl-4 border-l-2 border-border space-y-2">
                    <p>
                      <strong className="text-foreground">Vlad Ancuța Iulia – Cabinet Individual de Psihologie</strong><br />
                      Cod de înregistrare fiscală: 51414430<br />
                      Sediu profesional: Municipiul București, Str. Stănescu Gheorghe nr. 3
                    </p>
                    <p>
                      <strong className="text-foreground">Popescu Gheorghiu Nicoleta Sorina – Cabinet Individual de Psihologie</strong><br />
                      Cod de înregistrare fiscală: 34780539<br />
                      Sediu profesional: Strada 9 Mai nr. 3, bl. 37, ap. 37, Sector 6, București
                    </p>
                  </div>
                  <p>
                    În sensul Regulamentului (UE) 2016/679 privind protecția datelor cu caracter personal (GDPR), cele două cabinete individuale de psihologie acționează în calitate de operatori ai datelor prelucrate prin intermediul website-ului.
                  </p>
                </div>
              </div>

              <div>
                <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4">
                  2. Scopul acestei politici
                </h2>
                <div className="space-y-4">
                  <p>
                    Această politică explică:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>ce informații pot fi colectate atunci când utilizați website-ul;</li>
                    <li>în ce scop sunt utilizate;</li>
                    <li>care sunt drepturile dumneavoastră;</li>
                    <li>cum sunt protejate datele;</li>
                    <li>cum sunt utilizate tehnologiile de analiză și marketing online.</li>
                  </ul>
                  <p>
                    PSY BLOOM Institute este construit în jurul ideii de siguranță, claritate și responsabilitate profesională, iar protecția datelor personale face parte din aceste principii.
                  </p>
                </div>
              </div>

              <div>
                <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4">
                  3. Ce date colectăm
                </h2>
                <div className="space-y-4">
                  <p>
                    Website-ul nu colectează direct date personale prin formulare de contact, formulare de programare, newslettere sau conturi de utilizator.
                  </p>
                  <p>
                    Cu toate acestea, anumite informații tehnice pot fi colectate automat prin intermediul unor tehnologii de analiză și marketing online, precum:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>adresa IP;</li>
                    <li>informații despre dispozitiv și browser;</li>
                    <li>paginile vizitate;</li>
                    <li>durata vizitei;</li>
                    <li>interacțiuni cu website-ul;</li>
                    <li>sursa traficului;</li>
                    <li>evenimente de conversie și comportament pe website.</li>
                  </ul>
                  <p>
                    Aceste informații pot fi considerate date cu caracter personal atunci când permit identificarea directă sau indirectă a unei persoane.
                  </p>
                </div>
              </div>

              <div>
                <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4">
                  4. Tehnologii utilizate
                </h2>
                <div className="space-y-4">
                  <p>
                    Website-ul poate utiliza:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Meta Pixel;</li>
                    <li>tehnologii similare de analiză și măsurare a performanței campaniilor;</li>
                    <li>cookie-uri necesare funcționării website-ului;</li>
                    <li>cookie-uri statistice și de marketing, atunci când utilizatorul își exprimă acordul.</li>
                  </ul>
                  <p>
                    Meta Pixel permite măsurarea eficienței campaniilor publicitare și înțelegerea modului în care utilizatorii interacționează cu website-ul. Datele colectate pot fi transmise către Meta Platforms Ireland Limited.
                  </p>
                </div>
              </div>

              <div>
                <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4">
                  5. Consimțământul pentru cookie-uri și tehnologii de marketing
                </h2>
                <div className="space-y-4">
                  <p>
                    Pentru utilizatorii din Uniunea Europeană, cookie-urile și tehnologiile de marketing care nu sunt strict necesare funcționării website-ului sunt activate doar după exprimarea consimțământului explicit al utilizatorului. Consimțământul trebuie să fie informat, specific și exprimat printr-o acțiune clară.
                  </p>
                  <p>
                    Utilizatorul poate:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>accepta cookie-urile;</li>
                    <li>refuza cookie-urile opționale;</li>
                    <li>modifica ulterior opțiunile privind consimțământul.</li>
                  </ul>
                </div>
              </div>

              <div>
                <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4">
                  6. Butonul WhatsApp
                </h2>
                <div className="space-y-4">
                  <p>
                    Website-ul poate include un buton care permite contactarea directă prin WhatsApp.
                  </p>
                  <p>
                    Prin accesarea acestui buton, utilizatorul părăsește website-ul și este redirecționat către serviciul furnizat de WhatsApp.
                  </p>
                  <p>
                    Prelucrarea datelor efectuată după accesarea platformei WhatsApp este guvernată de politicile și termenii furnizorului respectiv.
                  </p>
                  <p>
                    PSY BLOOM Institute nu controlează și nu este responsabil pentru modul în care WhatsApp prelucrează datele utilizatorilor după redirecționare.
                  </p>
                </div>
              </div>

              <div>
                <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4">
                  7. Temeiul legal al prelucrării
                </h2>
                <div className="space-y-4">
                  <p>
                    Datele pot fi prelucrate în baza:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>consimțământului utilizatorului;</li>
                    <li>interesului legitim privind administrarea și securitatea website-ului;</li>
                    <li>obligațiilor legale aplicabile operatorilor.</li>
                  </ul>
                  <p>
                    În cazul cookie-urilor și tehnologiilor de marketing, temeiul principal îl reprezintă consimțământul utilizatorului.
                  </p>
                </div>
              </div>

              <div>
                <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4">
                  8. Destinatarii datelor
                </h2>
                <div className="space-y-4">
                  <p>
                    În funcție de tehnologiile utilizate, anumite informații pot fi transmise către:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>furnizori de găzduire web;</li>
                    <li>furnizori de servicii IT;</li>
                    <li>furnizori de servicii de analiză și marketing;</li>
                    <li>Meta Platforms Ireland Limited.</li>
                  </ul>
                  <p>
                    În anumite situații, datele pot fi transferate în afara Spațiului Economic European, în conformitate cu mecanismele legale aplicabile și garanțiile prevăzute de legislația europeană.
                  </p>
                </div>
              </div>

              <div>
                <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4">
                  9. Perioada de stocare
                </h2>
                <div className="space-y-4">
                  <p>
                    Datele sunt păstrate doar atât timp cât este necesar pentru:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>funcționarea website-ului;</li>
                    <li>îndeplinirea scopurilor pentru care au fost colectate;</li>
                    <li>respectarea obligațiilor legale.</li>
                  </ul>
                  <p>
                    Perioada exactă poate varia în funcție de tehnologia utilizată și de setările furnizorilor terți.
                  </p>
                </div>
              </div>

              <div>
                <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4">
                  10. Drepturile dumneavoastră
                </h2>
                <div className="space-y-4">
                  <p>
                    În conformitate cu GDPR, aveți dreptul:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>de acces la date;</li>
                    <li>la rectificarea datelor;</li>
                    <li>la ștergerea datelor;</li>
                    <li>la restricționarea prelucrării;</li>
                    <li>la opoziție;</li>
                    <li>la portabilitatea datelor;</li>
                    <li>de a retrage consimțământul în orice moment;</li>
                    <li>de a depune o plângere la autoritatea competentă.</li>
                  </ul>
                  <p>
                    În România, autoritatea competentă este:
                  </p>
                  <p className="pl-4 border-l-2 border-border">
                    <strong className="text-foreground">Autoritatea Națională de Supraveghere a Prelucrării Datelor cu Caracter Personal (ANSPDCP)</strong>
                  </p>
                </div>
              </div>

              <div>
                <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4">
                  11. Securitatea datelor
                </h2>
                <div className="space-y-4">
                  <p>
                    PSY BLOOM Institute implementează măsuri tehnice și organizatorice rezonabile pentru protejarea informațiilor împotriva:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>accesului neautorizat;</li>
                    <li>pierderii;</li>
                    <li>modificării;</li>
                    <li>divulgării accidentale sau ilegale.</li>
                  </ul>
                  <p>
                    Cu toate acestea, niciun sistem informatic nu poate garanta securitate absolută.
                  </p>
                </div>
              </div>

              <div>
                <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4">
                  12. Modificarea politicii
                </h2>
                <div className="space-y-4">
                  <p>
                    Ne rezervăm dreptul de a actualiza periodic această Politică de Confidențialitate pentru a reflecta modificări legislative, tehnice sau operaționale.
                  </p>
                  <p>
                    Versiunea actualizată va fi publicată pe această pagină și va produce efecte de la data publicării.
                  </p>
                </div>
              </div>

              <div>
                <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4">
                  13. Contact
                </h2>
                <p>
                  Pentru întrebări privind protecția datelor sau exercitarea drepturilor prevăzute de legislație, ne puteți contacta prin informațiile disponibile în pagina Contact a website-ului.
                </p>
              </div>

              <div className="pt-8 border-t border-border">
                <p className="text-base italic text-muted-foreground">
                  PSY BLOOM Institute este un spațiu construit în jurul clarității emoționale, siguranței relaționale și responsabilității profesionale. Același principiu stă la baza modului în care tratăm informațiile și datele persoanelor care ne vizitează website-ul.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* FOOTER */}
      <footer className="border-t border-border bg-[#32506c]">
        <div className="mx-auto max-w-6xl px-6 py-12">
          <nav aria-label="Navigare subsol" className="flex flex-wrap justify-center gap-x-8 gap-y-3 text-sm text-white/80 mb-8">
            <Link to="/" className="hover:text-white transition-colors">Acasă</Link>
            <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
            <a href="https://blog.psybloominstitute.ro" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Blog</a>
            <Link to="/termeni-si-conditii" className="hover:text-white transition-colors">Termeni și condiții</Link>
            <Link to="/politica-de-confidentialitate" className="hover:text-white transition-colors">Politica de confidentialitate</Link>
            <Link to="/politica-cookies" className="hover:text-white transition-colors">Politica cookies</Link>
          </nav>
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-6 border-t border-white/20 text-xs text-white/60">
            <p>© {new Date().getFullYear()} PSY BLOOM Institute</p>
            <p>
              Proiect creat de{" "}
              <a href="https://algoritmii.ro" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                Algoritmii.ro
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
